import React,{createContext,useContext,useState,useEffect,useCallback,useRef} from 'react';
import {fallbackData} from '../data/fallbackData';
import {fetchCmsData} from '../lib/googleSheets';
import activityTracker from '../lib/activityTracker';

const SiteContext=createContext();

export const useSiteContext=()=> useContext(SiteContext);

const getDataSignature=(data)=> {
  const refsSig=(data.websiteReferences || [])
    .map((r)=> `${r.websiteId}-${r.publicTitle}-${r.displayPriority}`)
    .join('|');
  const locationsSig=(data.seoLocations || [])
    .map((l)=> `${l.state}-${l.region}-${l.sortOrder}`)
    .join('|');
  const footerSig=(data.footerLinks || [])
    .map((f)=> `${f.title}-${f.url}-${f.sortOrder}`)
    .join('|');
  const settingsSig=JSON.stringify(data.settings || {});
  return `${refsSig}|${locationsSig}|${footerSig}|${settingsSig}`;
};

export const SiteProvider=({children})=> {
  const [data,setData]=useState(fallbackData);
  const [loading,setLoading]=useState(true);
  const [syncTickActive,setSyncTickActive]=useState(false);
  const [selectedReference,setSelectedReference]=useState(null);
  const [isModalOpen,setIsModalOpen]=useState(false);
  const [initialSyncActive,setInitialSyncActive]=useState(false);
  const lastSignatureRef=useRef(null);
  const syncIntervalRef=useRef(null);
  const inFlightRef=useRef(null);
  const lastSyncTimeRef=useRef(0);
  const cmsUrl=import.meta.env.VITE_BPAS_CMS_URL;
  const hasCmsUrl=!!cmsUrl;

  const triggerSyncTick=useCallback(()=> {
    setSyncTickActive(true);
    setTimeout(()=> setSyncTickActive(false),2500);
  },[]);

  const getSyncInterval=useCallback((settings)=> {
    const configured=settings?.syncIntervalSeconds;
    if (configured && typeof configured==='number') {
      return Math.max(30,Math.min(300,configured)) * 1000;
    }
    return 60000;
  },[]);

  const syncCmsData=useCallback(async (source='interval',timeoutMs=5000)=> {
    if (!hasCmsUrl || inFlightRef.current) return;

    const now=Date.now();
    if (source!=='initial' && now - lastSyncTimeRef.current < 45000) {
      return;
    }
    lastSyncTimeRef.current=now;

    try {
      inFlightRef.current=fetchCmsData({timeoutMs});
      const cmsData=await inFlightRef.current;
      const newSignature=getDataSignature(cmsData);
      const oldSignature=lastSignatureRef.current;
      const isFirstSync=!oldSignature;
      const dataChanged=newSignature !==oldSignature;

      setData((prevData)=> ({
        ...prevData,
        settings: {...prevData.settings,...cmsData.settings},
        websiteReferences: cmsData.websiteReferences,
        seoLocations: cmsData.seoLocations,
        footerLinks: cmsData.footerLinks,
      }));

      if (dataChanged || isFirstSync) {
        lastSignatureRef.current=newSignature;
        triggerSyncTick();
        activityTracker.trackCmsSync(true,dataChanged);
      }

      if (isFirstSync) {
        setInitialSyncActive(false);
      }
    } catch (error) {
      console.warn(`CMS sync failed (${source}):`,error.message);
      activityTracker.trackCmsSync(false,false);
      if (source==='initial') {
        setInitialSyncActive(false);
      }
    } finally {
      inFlightRef.current=null;
      if (source==='initial') {
        setLoading(false);
      }
    }
  },[hasCmsUrl,triggerSyncTick]);

  useEffect(()=> {
    if (hasCmsUrl) {
      setInitialSyncActive(false);
    }
    syncCmsData('initial',4500);
    activityTracker.trackPageView();

    if (hasCmsUrl) {
      const intervalMs=60000;
      syncIntervalRef.current=setInterval(()=> {
        syncCmsData('interval',5000);
      },intervalMs);
    }

    const handleVisibilityChange=()=> {
      if (document.visibilityState==='visible' && hasCmsUrl) {
        syncCmsData('visible',5000);
      }
    };

    const handleFocus=()=> {
      if (hasCmsUrl) {
        syncCmsData('focus',5000);
      }
    };

    const handleOnline=()=> {
      if (hasCmsUrl && navigator.onLine) {
        syncCmsData('online',5000);
      }
    };

    document.addEventListener('visibilitychange',handleVisibilityChange);
    window.addEventListener('focus',handleFocus);
    window.addEventListener('online',handleOnline);

    return ()=> {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      document.removeEventListener('visibilitychange',handleVisibilityChange);
      window.removeEventListener('focus',handleFocus);
      window.removeEventListener('online',handleOnline);
    };
  },[syncCmsData,hasCmsUrl]);

  const openModal=(reference)=> {
    setSelectedReference(reference);
    setIsModalOpen(true);
    activityTracker.trackModalOpened(reference.websiteId,reference.publicTitle);
  };

  const closeModal=()=> {
    setIsModalOpen(false);
  };

  const requestSimilar=()=> {
    if (selectedReference) {
      activityTracker.trackRequestSimilar(selectedReference.websiteId,selectedReference.publicTitle);
    }
    closeModal();
    const contactSection=document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({behavior: 'smooth'});
    }
  };

  const value={
    data,
    loading,
    syncTickActive,
    initialSyncActive,
    selectedReference,
    setSelectedReference,
    isModalOpen,
    openModal,
    closeModal,
    requestSimilar,
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};